import { showTextViewer } from "../../services/text-viewer-service";
import { dumpAsCsv, dumpAsJson, dumpAsSql, type DumpRow } from "./row-dump";

/**
 * What a selection action is handed: the rows it acts on (the selection, or every
 * row when nothing is selected), the visible columns in display order, and where
 * the rows came from when the grid knows.
 */
export type SelectionContext = {
  rows: DumpRow[];
  columns: string[];
  /** the table the rows came from (the firewall's logical name, or schema.table); absent for a join or an expression result */
  table?: string;
  /** true when the rows are an explicit selection, false when they are the whole result */
  selected: boolean;
  /** present when the grid is in edit mode on an editable result */
  edit?: {
    canDelete: boolean;
    /** the table has a soft-delete column */
    softDelete: boolean;
    /** stage a delete of rows; it is saved with the grid's other staged changes */
    stageDelete: (rows: DumpRow[], soft: boolean) => void;
  };
};

/**
 * A pluggable action over a grid's rows. `name` is the registry key; `available`
 * hides the action where it cannot apply (an action that needs a source table on a
 * result that has none).
 */
export type SelectionAction = {
  name: string;
  label: string;
  description?: string;
  available?: (ctx: SelectionContext) => boolean;
  run: (ctx: SelectionContext) => void | Promise<void>;
};

/**
 * Registry of selection actions — the same singleton-over-a-Map shape as
 * FormWidgetService. An action registers itself as an import side effect; a grid
 * lists the registry and offers what is available for its rows
 * (FEATURE-20261013-sql-query-edit-data-and-selection-actions-over-the-mesh).
 */
export class SelectionActionService {
  static readonly _instance: SelectionActionService = new SelectionActionService();

  static get() {
    return this._instance;
  }

  private actions = new Map<string, SelectionAction>();

  register(action: SelectionAction) {
    this.actions.set(action.name, action);
  }

  /** Every registered action available for ctx, in registration order. */
  for(ctx: SelectionContext): SelectionAction[] {
    return [...this.actions.values()].filter((a) => !a.available || a.available(ctx));
  }
}

// ── deletes: staged in the grid's change set, saved with its other changes ──

SelectionActionService.get().register({
  name: "delete",
  label: "Delete",
  description: "soft delete: marks the rows deleted",
  available: (ctx) => ctx.selected && Boolean(ctx.edit?.canDelete && ctx.edit.softDelete),
  run: (ctx) => ctx.edit?.stageDelete(ctx.rows, true),
});

SelectionActionService.get().register({
  name: "delete-permanently",
  label: "Delete permanently",
  description: "removes the rows from the table",
  available: (ctx) => ctx.selected && Boolean(ctx.edit?.canDelete),
  run: (ctx) => ctx.edit?.stageDelete(ctx.rows, false),
});

// ── the built-in dumps: each opens its text in the read-only viewer, which copies ──

const title = (what: string, ctx: SelectionContext) =>
  `${what} · ${ctx.rows.length} ${ctx.selected ? "selected " : ""}row${ctx.rows.length === 1 ? "" : "s"}`;

SelectionActionService.get().register({
  name: "dump-sql",
  label: "Dump as SQL",
  description: "INSERT statements",
  run: (ctx) =>
    showTextViewer({ text: dumpAsSql(ctx.rows, ctx.columns, ctx.table ?? "table_name"), language: "sql", title: title("SQL", ctx) }),
});

SelectionActionService.get().register({
  name: "dump-csv",
  label: "Dump as CSV",
  description: "header line, RFC 4180 quoting",
  run: (ctx) => showTextViewer({ text: dumpAsCsv(ctx.rows, ctx.columns), language: "plaintext", title: title("CSV", ctx) }),
});

SelectionActionService.get().register({
  name: "dump-json",
  label: "Dump as JSON",
  description: "an array of objects",
  run: (ctx) => showTextViewer({ text: dumpAsJson(ctx.rows, ctx.columns), language: "json", title: title("JSON", ctx) }),
});
